import { ArrowSquareOutIcon, BarcodeIcon } from '@phosphor-icons/react';
import { Link } from 'react-router';
import type { OrderError } from '@skupreme/api-contracts/core';
import { cn } from '@skupreme/shared/ui';
import { Tabs } from '@utility/constants/common';
import { getLabelIssue, getMissingDimensionSkus } from '../../lib/issues';

/** An order line seen only through what a SKU link needs. */
export type LabelBlockedItem = {
  sku?: string;
  item_id?: string;
};

export type LabelBlockedBannerProps = {
  errors: OrderError[] | undefined;
  /** Used only to turn a named SKU into a catalog link; order does not matter. */
  items?: LabelBlockedItem[];
  className?: string;
};

/** Only a real http(s) destination is safe to open; anything else is refused. */
function safeLink(value: string | undefined): string | undefined {
  return value && /^https:\/\//i.test(value) ? value : undefined;
}

const LINK_CLASS =
  'sk:font-medium sk:text-danger sk:underline sk:hover:text-danger';

/**
 * Why a label could not be bought automatically, and the two ways out: fill the
 * package attributes in, or enter them once on the carrier's own form.
 */
export function LabelBlockedBanner({
  errors,
  items,
  className,
}: LabelBlockedBannerProps) {
  const issue = getLabelIssue(errors);

  if (!issue) {
    return null;
  }

  const { skus: rawSkus, overflow } = getMissingDimensionSkus(issue.reason);
  const skus = [...new Set(rawSkus)];
  const link = safeLink(issue.error_details);
  const catalogId = (sku: string) =>
    items?.find(item => item.sku === sku)?.item_id;

  return (
    <div
      role="alert"
      className={cn(
        'sk:flex sk:items-start sk:gap-3 sk:rounded-card sk:bg-danger-bg sk:px-4 sk:py-3 sk:text-danger',
        className,
      )}
    >
      <BarcodeIcon size={18} weight="bold" className="sk:mt-0.5 sk:shrink-0" />

      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1">
        <p className="sk:m-0 sk:text-dense sk:font-medium">
          {skus.length || overflow > 0
            ? 'Missing package dimensions — this label cannot be generated automatically.'
            : 'This order needs manual packaging before a label can be generated.'}
        </p>

        {skus.length || overflow > 0 ? (
          <p className="sk:m-0 sk:text-dense">
            {skus.length ? (
              <>
                Add weight and dimensions for{' '}
                {skus.map((sku, index) => {
                  const id = catalogId(sku);

                  return (
                    <span key={sku}>
                      {index > 0 ? ', ' : null}
                      {id ? (
                        <Link
                          to={`/app/catalogs/catalog/${encodeURIComponent(id)}/?tab=${Tabs.catalog.packageAttributes}`}
                          className={LINK_CLASS}
                        >
                          {sku}
                        </Link>
                      ) : (
                        <span className="sk:font-medium">{sku}</span>
                      )}
                    </span>
                  );
                })}
                {overflow > 0 ? ` and ${overflow} more` : null}
              </>
            ) : (
              `Add weight and dimensions for ${overflow} item${overflow === 1 ? '' : 's'}.`
            )}
          </p>
        ) : null}
      </div>

      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="sk:inline-flex sk:shrink-0 sk:items-center sk:gap-1.5 sk:rounded-input sk:bg-danger sk:px-3 sk:py-1.5 sk:text-dense sk:font-medium sk:text-white sk:no-underline sk:hover:bg-danger/90"
        >
          Enter dimensions
          <ArrowSquareOutIcon size={14} />
        </a>
      ) : null}
    </div>
  );
}
